// The lead agent (modelled on Explee): it reads the seller's website and its won customers, says what the seller
// sells, proposes buyer segments with search filters, ranks what the engines found, and drafts a first email per
// lead. Nobody types keywords. Shared by the Leads page (on click) and the nightly run. Pure apart from zod.
import { z } from "zod";

import {
  domainOf,
  LeadFiltersSchema,
  type KeyEngine,
  type Lead,
  type LeadEngine,
} from "./leadFilters.ts";
import { isOpenEngine, wikidataPlan, type OpenEngine } from "./openData.ts";
import { GREETING } from "./outreach.ts";
import { untrusted, UNTRUSTED_NOTE } from "./untrusted.ts";

export const DEFAULT_OUR_WEBSITE = "ithnk.com";
export const MAX_SEGMENTS = 5;
/** Criteria per segment: each lead is scored 0-5 against every positive one */
export const MAX_CRITERIA = 4;
export const MAX_BROCHURES = 2;
const SITE_CHARS = 12_000;
const LEAD_SITE_CHARS = 4_000;

/** A brochure the agent may point a lead to (Settings, Lead agent) */
export type Brochure = { name: string; url: string; about?: string };
/** A won customer, as the agent sees it */
export type Customer = {
  name: string;
  sector?: string | null;
  size?: number | null;
  country?: string | null;
};

const percent = z.coerce.number().min(0).max(100).catch(50);

/** The filter list, cut to its maximum first (an AI answer often gives one too many) */
const tolerant = <T extends z.ZodTypeAny>(schema: T, max: number) =>
  z
    .preprocess(
      (value) => (Array.isArray(value) ? value.slice(0, max) : value),
      schema,
    )
    .catch([] as z.infer<T>) as unknown as T;

const shortList = (max: number, chars = 160) =>
  z
    .array(z.string().trim().min(1).max(chars))
    .catch([])
    .default([])
    .transform((items) => items.slice(0, max));

/**
 * A segment and its brief (as Explee's campaigns: the problem, who to write to, what must and must not be true of a
 * lead, and clients like the ones wanted). The user reviews and edits it before anything is searched.
 */
export const SegmentSchema = LeadFiltersSchema.extend({
  name: z.string().trim().min(1).max(80),
  why: z.string().trim().max(400).catch(""),
  fit: percent,
  target: z.enum(["companies", "people"]).catch("companies"),
  employee_ranges: LeadFiltersSchema.shape.employee_ranges.catch([]),
  // an AI answer with too many keywords or places keeps the first ones instead of failing the whole plan
  keywords: tolerant(LeadFiltersSchema.shape.keywords, 5),
  job_titles: tolerant(LeadFiltersSchema.shape.job_titles, 5),
  locations: tolerant(LeadFiltersSchema.shape.locations, 3),
  customer_problem: z.string().trim().max(400).catch("").default(""),
  target_role: z.string().trim().max(120).catch("").default(""),
  positive_criteria: shortList(MAX_CRITERIA),
  negative_criteria: shortList(MAX_CRITERIA),
  example_clients: shortList(5, 80),
});
export type Segment = z.infer<typeof SegmentSchema>;

export const SegmentsSchema = z.object({
  offer: z.string().trim().max(600).catch(""),
  segments: z
    .array(SegmentSchema)
    .min(1)
    .transform((s) => s.slice(0, MAX_SEGMENTS)),
});
export type SegmentsAnswer = z.infer<typeof SegmentsSchema>;

const pages = (site: { url: string; text: string }[], max: number) =>
  site
    .map((p) => `${p.url}\n${p.text}`)
    .join("\n\n")
    .slice(0, max);

export const buildSegmentsPrompt = (input: {
  website: string;
  site: { url: string; text: string }[];
  customers: Customer[];
  profile?: string;
  /** what this run should explore (a market, sector or service), typed by the user or the nightly run */
  focus?: string;
  /** leads the user marked "Not a fit", with the reason: never propose their like again */
  rejections?: Rejection[];
}) =>
  [
    "You are the lead research agent of a B2B company. Read its website and its won customers, then:",
    "offer: say in one or two sentences what the company sells and the problems it solves for clients.",
    `segments: up to ${MAX_SEGMENTS} groups of organisations most likely to buy that work now, best first. A buyer is an organisation`,
    "whose core business is not technology and that is large enough to pay for advice: banks, insurers, fintechs, hospitals,",
    "government bodies, telecoms, manufacturers, retailers, schools and the like. Never propose technology firms: software",
    "houses, IT service providers, system integrators, digital or marketing agencies, or technology and management",
    "consultancies. They build or sell this work themselves and do not hire the company. For each segment:",
    '  name: a short label (for example "Family-owned manufacturers in Egypt");',
    "  why: one sentence on why they need this company's work;",
    "  customer_problem: the problem they have now that this company's work solves, in one sentence;",
    '  target_role: the person to write to (for example "Chief Information Officer");',
    `  positive_criteria: up to ${MAX_CRITERIA} short, checkable facts that tell the members of this segment who need the work NOW`,
    "  apart from the rest: signals of need or timing (a transformation or core system programme announced, a new",
    "  regulation to comply with, a merger, rapid growth or expansion, a new CIO or CDO, a public tender, at least one",
    '  size floor such as "500+ employees"). Never a fact every member of the segment meets (not "is a bank");',
    `  negative_criteria: up to ${MAX_CRITERIA} facts that rule a lead out (for example "is a software or IT company",`,
    '  "is a branch or outlet", "has under 50 employees");',
    "  example_clients: up to 5 real organisations in this segment that would be ideal clients;",
    "  fit: 0-100, how likely a company in this segment buys;",
    '  target: "people" when a named decision-maker should be contacted, else "companies";',
    '  keywords: up to 3 names for the buyer\'s own kind of organisation as a map lists it (for example "bank", "hospital",',
    '  "insurance company", "ministry", "factory"); never words for what the company sells ("digital transformation",',
    '  "software", "IT", "consulting"), which find its competitors; job_titles: up to 5 titles of the decision-makers;',
    '  locations: up to 3 real places as "City, Country" or a country name (never a region such as "MENA" or "GCC": name its countries), where the company sells (from its website or customers);',
    '  employee_ranges: any of "1,10", "11,50", "51,200", "201,500", "501,1000", "1001,5000", "5001,10000".',
    "Prefer segments like the won customers, and markets the website names.",
    input.focus
      ? `This run focuses on: ${input.focus.slice(0, 300)}. Every segment must fit that focus.`
      : "",
    'Answer with JSON only: {"offer": "...", "segments": [{"name": "...", "why": "...", "fit": 80, "target": "people", "customer_problem": "...", "target_role": "...", "positive_criteria": [], "negative_criteria": [], "example_clients": [], "keywords": [], "job_titles": [], "locations": [], "employee_ranges": []}]}',
    UNTRUSTED_NOTE,
    untrusted(`website ${input.website}`, pages(input.site, SITE_CHARS)),
    untrusted(
      "won customers",
      input.customers.length
        ? input.customers
            .map((c) =>
              [c.name, c.sector, c.size && `${c.size} staff`, c.country]
                .filter(Boolean)
                .join(", "),
            )
            .join("\n")
        : "none yet",
    ),
    input.profile ? untrusted("ideal customer profile", input.profile) : "",
    input.rejections?.length
      ? untrusted(
          "leads the seller rejected: learn the pattern, add it to negative_criteria",
          rejectionsText(input.rejections),
        )
      : "",
  ]
    .filter(Boolean)
    .join("\n");

/** A lead the user marked "Not a fit", kept in Settings (lead_rejections) and fed to the next runs */
export type Rejection = {
  name: string;
  domain?: string;
  industry?: string;
  segment?: string;
  reason?: string;
};
export const MAX_REJECTIONS = 60;

export const rejectionsText = (rejections: Rejection[]) =>
  rejections
    .slice(-MAX_REJECTIONS)
    .map((r) =>
      [
        r.name,
        r.industry,
        r.domain,
        r.segment && `segment: ${r.segment}`,
        r.reason && `why: ${r.reason}`,
      ]
        .filter(Boolean)
        .join(" | "),
    )
    .join("\n");

/** The latest rejections first, one per company, at most MAX_REJECTIONS */
export const addRejection = (list: Rejection[] = [], rejection: Rejection) =>
  [
    ...list.filter(
      (r) =>
        (r.domain ?? r.name).toLowerCase() !==
        (rejection.domain ?? rejection.name).toLowerCase(),
    ),
    rejection,
  ].slice(-MAX_REJECTIONS);

/** A lead the user already rejected (same domain, else same name) */
export const isRejected = (lead: Lead, list: Rejection[] = []) =>
  list.some((r) =>
    r.domain && lead.company.domain
      ? r.domain.toLowerCase() === lead.company.domain.toLowerCase()
      : r.name.trim().toLowerCase() === lead.company.name.trim().toLowerCase(),
  );

/**
 * The engines to search for a segment, given the engines that have a key: companies on Apollo (or Google Maps
 * when Apollo has no key), decision-makers on Apollo.
 */
export const segmentEngines = (
  segment: Segment,
  keyed: ReadonlySet<KeyEngine>,
): (LeadEngine | OpenEngine)[] => {
  // Wikidata first when it knows the segment's kind and country: head offices with their own websites, free
  const open: OpenEngine[] = wikidataPlan(segment) ? ["wikidata_orgs"] : [];
  return [...open, ...keyedEngines(segment, keyed)];
};

const keyedEngines = (
  segment: Segment,
  keyed: ReadonlySet<KeyEngine>,
): LeadEngine[] => {
  const engines: LeadEngine[] = [];
  if (segment.target === "people") {
    if (keyed.has("apollo")) engines.push("apollo_people");
  }
  if (!engines.length) {
    if (keyed.has("apollo")) engines.push("apollo_companies");
    else if (keyed.has("apify") && segment.keywords.length)
      engines.push("apify_maps");
  }
  return engines;
};

/** Head offices from a free register after which the map search (mostly branches of large organisations) is skipped */
export const ENOUGH_HEAD_OFFICES = 5;

/** Pages a search of the engine takes for a segment of that many pages (its weight): only the keyed people and company
 * searches page; a free register and the map search answer once */
const pagesOf = (engine: LeadEngine | OpenEngine, pages: number) =>
  isOpenEngine(engine) || engine === "apify_maps" ? 1 : pages;

/** The segment's next engine to search, or undefined when every one was tried (the map search is skipped once a
 * free register gave enough head offices). `tried` holds an engine once per page searched; a segment the User
 * scaled searches `pages` pages of each keyed engine, a look-up each. */
export const nextEngine = (
  segment: Segment,
  keyed: ReadonlySet<KeyEngine>,
  tried: readonly string[],
  headOffices: number,
  pages = 1,
) =>
  segmentEngines(segment, keyed).find(
    (e) =>
      tried.filter((t) => t === e).length < pagesOf(e, pages) &&
      !(e === "apify_maps" && headOffices >= ENOUGH_HEAD_OFFICES),
  );

/** A failed search ends its engine for the segment: no further page costs a look-up */
export const engineDone = (tried: string[], engine: string, pages: number) =>
  tried.push(...Array<string>(pages).fill(engine));

/** The engine's plan or key refuses API searches (lead_search passes on the engine's 401 or 403) */
export const engineRefused = (message: string) =>
  /answered 40[13]\b/.test(message);

const score = z.coerce.number().min(0).max(5).catch(0);

export const RankSchema = z.object({
  ranks: z.array(
    z.object({
      ref: z.string(),
      fit: percent,
      reason: z.string().trim().max(300).catch(""),
      /** 0-5 against each positive criterion, in order */
      scores: z.array(score).catch([]).default([]),
      /** the most recent dated fact (news or website) that shows the lead needs the work now; "" when none */
      why_now: z.string().trim().max(300).catch("").default(""),
      /** the negative criteria the lead meets, by number (1-based) */
      ruled_out: z.array(z.coerce.number().int()).catch([]).default([]),
      /** the organisation's headcount, estimated from public knowledge (the map gives none) */
      employees: z.coerce.number().int().positive().nullish().catch(null),
    }),
  ),
});

export type RankAnswer = z.infer<typeof RankSchema>;

export const buildRankPrompt = (
  offer: string,
  segment: Segment,
  leads: Lead[],
  extra: {
    /** what each lead's own website says, by ref (read for the strongest candidates) */
    sites?: Record<string, string>;
    /** recent dated headlines about each lead, by ref */
    news?: Record<string, string>;
    /** dated job ads, tenders, regulator rules and reports for each lead, by ref (/api/signals) */
    signals?: Record<string, Signal[]>;
    rejections?: Rejection[];
  } = {},
) =>
  [
    "A B2B company is looking for clients. Rate each lead 0-100 for how likely it needs this company's work now,",
    "with a one-sentence reason a salesperson can act on, specific to that lead (not a generic phrase).",
    segment.positive_criteria.length
      ? `scores: for each lead, 0-5 against each criterion below, in order (5 = clearly true, 0 = clearly false, 2 = unknown: no evidence either way):\n${segment.positive_criteria.map((c, i) => `  ${i + 1}. ${c}`).join("\n")}`
      : "",
    segment.negative_criteria.length
      ? `ruled_out: the numbers of these that are true of the lead (any one rules it out):\n${segment.negative_criteria.map((c, i) => `  ${i + 1}. ${c}`).join("\n")}`
      : "",
    "Judge from the lead's own website text, recent news and signals when given, else from what is publicly known; do not guess.",
    `why_now: the most recent dated fact from the news, signals or website given that shows the lead needs this work now ${SIGNALS_NOTE}`,
    '(for example "2026-09: announced a core banking replacement"), or "" when nothing given shows it.',
    "Under 20: a foreign organisation's local branch, representative office or subsidiary unit, and a division, product or",
    "wealth/investment arm of an organisation (the buyer is the parent; name it in the reason).",
    "Under 20: competitors and anyone whose business is technology (software houses, IT services, system integrators, digital",
    "agencies, technology or management consultancies: they do this work themselves); associations, NGOs and chambers;",
    "small offices and shops (a sole accountant, a store); and a branch, ATM or outlet of a larger organisation (the buyer is",
    "its head office; name the head office in the reason). Prefer head offices of organisations large enough to pay for advice.",
    "employees: your best estimate of the organisation's total headcount from what is publicly known (a national bank",
    "has thousands), or null when you cannot tell.",
    'Answer with JSON only: {"ranks": [{"ref": "...", "fit": 75, "reason": "...", "why_now": "", "scores": [5, 3], "ruled_out": [], "employees": 5000}]}, one entry per lead, same ref.',
    UNTRUSTED_NOTE,
    untrusted("what the company sells", offer),
    untrusted(
      "segment",
      [
        `${segment.name}: ${segment.why}`,
        segment.customer_problem && `problem: ${segment.customer_problem}`,
        segment.example_clients.length &&
          `ideal clients look like: ${segment.example_clients.join(", ")}`,
      ]
        .filter(Boolean)
        .join("\n"),
    ),
    extra.rejections?.length
      ? untrusted(
          "leads the seller rejected (score their like under 20)",
          rejectionsText(extra.rejections),
        )
      : "",
    untrusted(
      "leads",
      leads
        .map((l) =>
          [
            `ref ${l.ref}`,
            l.company.name,
            l.company.industry,
            l.company.employees && `${l.company.employees} staff`,
            [l.company.city, l.company.country].filter(Boolean).join(", "),
            l.company.domain,
            l.contact &&
              `contact: ${l.contact.first_name} ${l.contact.last_name ?? ""}, ${l.contact.title ?? ""}`,
            extra.sites?.[l.ref] &&
              `website says: ${extra.sites[l.ref].slice(0, SITE_PER_LEAD)}`,
            extra.news?.[l.ref] && `recent news: ${extra.news[l.ref]}`,
            extra.signals?.[l.ref]?.length &&
              `signals: ${signalsText(extra.signals[l.ref])}`,
          ]
            .filter(Boolean)
            .join(" | "),
        )
        .join("\n"),
    ),
  ]
    .filter(Boolean)
    .join("\n");

const SITE_PER_LEAD = 1_200;

export type Signal = {
  kind: "job" | "tender" | "regulation" | "report";
  date: string;
  title: string;
  url?: string;
  source: string;
};
/** What /api/signals reads a company by */
export type SignalQuery = {
  company: string;
  domain?: string;
  country?: string;
  industry?: string;
};
/** Signal text per lead in a prompt: the Worker sends at most three of each kind, this caps the rest */
export const SIGNALS_PER_LEAD = 700;
export const signalsText = (items: Signal[]) =>
  items
    .map((s) => `${s.kind} ${s.date}: ${s.title} (${s.source})`)
    .join("; ")
    .slice(0, SIGNALS_PER_LEAD);
const SIGNALS_NOTE =
  "(signals: a job ad for a technology role, a tender, a rule of the lead's sector regulator that it must meet, or its latest annual report also count)";

/**
 * The fit of a ranked lead: the criteria scores (their mean as a percentage) averaged with the model's own judgement
 * of need, so a lead that meets every criterion but that the model judges unlikely (a regulator) is not at the top;
 * capped at 15 when a negative criterion holds; the model's fit alone when it gave no scores
 */
export const fitOf = (
  rank: { fit: number; scores: number[]; ruled_out: number[] },
  segment: Segment,
) => {
  const ruledOut = rank.ruled_out.some(
    (n) => n >= 1 && n <= segment.negative_criteria.length,
  );
  const scores = rank.scores.slice(0, segment.positive_criteria.length);
  const fit =
    segment.positive_criteria.length && scores.length
      ? Math.round(
          ((scores.reduce((a, b) => a + b, 0) /
            (5 * segment.positive_criteria.length)) *
            100 +
            rank.fit) /
            2,
        )
      : rank.fit;
  return ruledOut ? Math.min(fit, 15) : fit;
};

/** Rows ranked below this are not shown: they are not worth a salesperson's time */
export const MIN_FIT = 40;

// What never buys this work, by name, map category or industry. ponytail: word lists; the AI ranking catches what they
// miss, and they grow from what the founder rejects.
// Technology firms do this work themselves (competitors)...
const TECH_FIRM =
  /\b(software|it (services?|solutions|company)|information technology|tech(nology)? (company|solutions|services)|system(s)? integrat|web (design|development)|app(lication)? develop|programming|digital (agency|marketing|solutions|transformation)|marketing agency|advertising agency|consult(ancy|ancies|ing|ant|ants))\b/i;
// ...but Maps files payment and lending fintechs under "Software company", and they are buyers (SOPs, compliance, data).
const FINANCIAL =
  /(fin ?tech|pay(ment)?s?\b|wallet|lending|financ|insurance|takaful|\bbank|banque)/i;
// Member bodies, shops, branches and small professional offices never buy, financial or not.
const NEVER =
  /\b(association|non-?profit|ngo|chamber|syndicate|store|shop|showroom|branch|atm|kiosk|accountants?|accounting|bookkeep|law (firm|office)|lawyer|attorney)\b|فرع|صراف|ماكينة/i;
// A social page as the only website: a small business.
const SOCIAL_ONLY =
  /(^|\.)(facebook|instagram|linkedin|twitter|x|tiktok|wa)\.(com|me)$/i;

/** A lead that never buys this work: a technology firm (unless financial), a member body, shop, branch or small office */
export const notABuyer = (lead: Lead) => {
  const what = `${lead.company.name} | ${lead.company.industry ?? ""}`;
  return (
    NEVER.test(what) ||
    SOCIAL_ONLY.test(lead.company.domain ?? "") ||
    (TECH_FIRM.test(what) &&
      !FINANCIAL.test(`${lead.company.name} ${lead.company.domain ?? ""}`))
  );
};

/** The filters to search a segment with: a map search takes place names only, so job titles stay out of it */
export const searchFilters = (
  segment: Segment,
  engine: LeadEngine | OpenEngine,
) => (engine === "apify_maps" ? { ...segment, job_titles: [] } : segment);

export type RankedLead = {
  lead: Lead;
  segment: string;
  fit: number;
  reason: string;
  /** the lead's 0-5 score against each of its segment's criteria (null: not scored) */
  criteria?: { criterion: string; score: number | null }[];
  /** ranked a second time with what its own website and recent news say */
  checked?: boolean;
  /** the dated fact that shows need now (news or website) */
  why_now?: string;
  /** what was read before scoring, saved as the pre-lead's evidence */
  news?: NewsItem[];
  signals?: Signal[];
  site_summary?: string;
};

/** One row per company (by domain, else name), the best-ranked kept, best first */
export const mergeRanked = (rows: RankedLead[]): RankedLead[] => {
  const best = new Map<string, RankedLead>();
  for (const row of rows) {
    const id = (row.lead.company.domain ?? row.lead.company.name)
      .trim()
      .toLowerCase();
    const seen = best.get(id);
    const top = !seen || row.fit > seen.fit ? row : seen;
    // the head office's own record (Wikidata) over a map listing of one of its branches, at the best fit
    const record = [row, seen].find((r) => r?.lead.source === "wikidata");
    best.set(
      id,
      record && record !== top ? { ...top, lead: record.lead } : top,
    );
  }
  return [...best.values()].sort((a, b) => b.fit - a.fit);
};

/** Leads per ranking call: a model asked about 40 at once answers them all alike */
export const RANK_BATCH = 10;
/** Candidates whose website, recent news and signals are read at the same time */
export const EVIDENCE_AT_ONCE = 3;

export type NewsItem = {
  title: string;
  date: string;
  source: string;
  url?: string;
};
export type Evidence = {
  sites: Record<string, string>;
  news: Record<string, string>;
  headlines: Record<string, NewsItem[]>;
  signals: Record<string, Signal[]>;
};

/** The start of a website's text, whitespace collapsed: the pre-lead's Evidence tab shows it */
export const siteExcerpt = (text: string, max = 300) => {
  const flat = text.replace(/\s+/g, " ").trim();
  return flat.length > max ? `${flat.slice(0, max).trimEnd()}…` : flat;
};

/**
 * Evidence for every candidate (all free): its own website, the last year's news and the signals beyond it (job ads,
 * tenders, regulator rules, annual report), a few at once; scoring without evidence answered every bank alike. The
 * browser reads through the Worker with the session, the nightly run with the server secret.
 */
export const gatherEvidence = async (
  candidates: Lead[],
  read: {
    site: (website: string) => Promise<{ pages: { text: string }[] } | null>;
    news: (query: string) => Promise<NewsItem[]>;
    signals: (company: SignalQuery) => Promise<Signal[]>;
  },
  atOnce = EVIDENCE_AT_ONCE,
): Promise<Evidence> => {
  const evidence: Evidence = {
    sites: {},
    news: {},
    headlines: {},
    signals: {},
  };
  for (let k = 0; k < candidates.length; k += atOnce)
    await Promise.all(
      candidates.slice(k, k + atOnce).map(async (lead) => {
        const { company } = lead;
        const [site, items, signals] = await Promise.all([
          company.website ? read.site(company.website).catch(() => null) : null,
          read.news(`"${company.name}" ${NEWS_TOPICS}`),
          read
            .signals({
              company: company.name,
              domain: company.domain ?? domainOf(company.website) ?? undefined,
              country: company.country,
              industry: company.industry,
            })
            .catch(() => []),
        ]);
        const text = site?.pages.map((p) => p.text).join("\n");
        if (text) evidence.sites[lead.ref] = text;
        if (signals.length) evidence.signals[lead.ref] = signals;
        if (items.length) {
          evidence.headlines[lead.ref] = items;
          evidence.news[lead.ref] = items
            .map((i) => `${i.date}: ${i.title} (${i.source})`)
            .join("; ");
        }
      }),
    );
  return evidence;
};

/** Score leads against the segment's criteria, a batch at a time (parallel calls trip the AI's rate limit); a failed
 * batch leaves its leads unscored and `failed` hears why */
export const rankLeads = async (
  ask: (prompt: string) => Promise<RankAnswer>,
  offer: string,
  segment: Segment,
  leads: Lead[],
  extra: {
    sites?: Record<string, string>;
    news?: Record<string, string>;
    signals?: Record<string, Signal[]>;
    rejections?: Rejection[];
  },
  failed: (error: Error) => void,
) => {
  const answers: RankAnswer[] = [];
  for (let i = 0; i < leads.length; i += RANK_BATCH)
    answers.push(
      await ask(
        buildRankPrompt(offer, segment, leads.slice(i, i + RANK_BATCH), extra),
      ).catch((error) => {
        failed(error as Error);
        return { ranks: [] as RankAnswer["ranks"] };
      }),
    );
  return new Map(answers.flatMap((a) => a.ranks).map((r) => [r.ref, r]));
};

/** The ranked candidates worth a salesperson's time (fit MIN_FIT or more), with what was read about each */
export const rankedRows = (
  segment: Segment,
  candidates: Lead[],
  ranked: Map<string, RankAnswer["ranks"][number]>,
  evidence: Evidence,
): RankedLead[] =>
  candidates.flatMap((lead) => {
    const r = ranked.get(lead.ref);
    // ponytail: an unranked lead gets half its segment's fit
    const fit = r ? fitOf(r, segment) : Math.round(segment.fit / 2);
    if (fit < MIN_FIT) return [];
    return [
      {
        // the estimate fills the size the map leaves empty
        lead:
          lead.company.employees || !r?.employees
            ? lead
            : { ...lead, company: { ...lead.company, employees: r.employees } },
        segment: segment.name,
        fit,
        reason: r?.reason || segment.why,
        criteria: segment.positive_criteria.map((criterion, i) => ({
          criterion,
          score: r?.scores[i] ?? null,
        })),
        checked: !!(
          evidence.sites[lead.ref] ||
          evidence.news[lead.ref] ||
          evidence.signals[lead.ref]
        ),
        why_now: r?.why_now || undefined,
        news: evidence.headlines[lead.ref],
        signals: evidence.signals[lead.ref],
        site_summary:
          evidence.sites[lead.ref] && siteExcerpt(evidence.sites[lead.ref]),
      },
    ];
  });

/** The pre_leads row for a ranked lead (browser and nightly run); the segment snapshot carries the agent's offer text */
export const preLeadRow = (
  row: RankedLead,
  runId: string,
  segment: Segment | undefined,
  offer: string,
) => {
  const { company, contact } = row.lead;
  return {
    run_id: runId,
    segment_name: row.segment,
    segment: { ...segment, offer },
    source: row.lead.source,
    ref: row.lead.ref,
    company_name: company.name,
    website: company.website ?? null,
    domain: company.domain ?? domainOf(company.website) ?? null,
    country: company.country ?? null,
    city: company.city ?? null,
    industry: company.industry ?? null,
    employees: company.employees ?? null,
    linkedin_url: company.linkedin_url ?? null,
    fit: Math.round(row.fit),
    criteria: (row.criteria ?? []).flatMap((c) =>
      c.score === null ? [] : [{ name: c.criterion, score: c.score }],
    ),
    why: row.reason,
    why_now: row.why_now ?? null,
    checked: row.checked ?? false,
    evidence: {
      log: [] as string[],
      news: row.news ?? [],
      ...(row.signals?.length && { signals: row.signals }),
      site_summary: row.site_summary || null,
    },
    contact_first_name: contact?.first_name ?? null,
    contact_last_name: contact?.last_name ?? null,
    contact_title: contact?.title ?? null,
    contact_email: contact?.email ?? null,
    contact_linkedin_url: contact?.linkedin_url ?? null,
    status: "found" as const,
  };
};

/** A saved pre-lead as the draft prompt sees a ranked lead, written to `recipient` (Keep and the nightly run) */
export const rankedFromRow = (
  row: {
    id: number | string;
    ref?: string | null;
    source?: string | null;
    company_name: string;
    website?: string | null;
    domain?: string | null;
    city?: string | null;
    country?: string | null;
    industry?: string | null;
    segment_name?: string | null;
    fit?: number | null;
    why?: string | null;
  },
  recipient: {
    first_name: string;
    last_name?: string;
    title?: string | null;
  } | null,
): RankedLead => ({
  lead: {
    ref: row.ref ?? String(row.id),
    source: (row.source ?? "wikidata") as Lead["source"],
    company: {
      name: row.company_name,
      website: row.website ?? undefined,
      domain: row.domain ?? undefined,
      city: row.city ?? undefined,
      country: row.country ?? undefined,
      industry: row.industry ?? undefined,
    },
    contact: recipient
      ? {
          first_name: recipient.first_name,
          last_name: recipient.last_name,
          title: recipient.title ?? undefined,
        }
      : undefined,
  },
  segment: row.segment_name ?? "",
  fit: row.fit ?? 0,
  reason: row.why ?? "",
});

/**
 * The brief the nightly run searches with: the segments the User last searched on the Leads page (Settings keeps it),
 * the segment names the User paused (neither run searches them), the pages per keyed engine the User gave a segment
 * (weights, 1 when absent) and the suggestions the User dismissed (segment name: label). Only the User's clicks
 * change them (segmentResults.ts).
 */
export type NightPlan = {
  offer: string;
  segments: Segment[];
  paused?: string[];
  weights?: Record<string, number>;
  dismissed?: Record<string, string>;
};

/** What a headline must touch to count as a sign of need (the news query for a company) */
export const NEWS_TOPICS =
  '(transformation OR digital OR "core banking" OR ERP OR tender OR RFP OR AI OR cloud OR data OR cybersecurity OR strategy)';

export const WhyNowSchema = z.object({
  why_now: z.string().trim().max(300).catch("").default(""),
});

/** One lead's why-now fact from its last year of news and its signals (Keep, for a pre-lead that arrived without one) */
export const buildWhyNowPrompt = (input: {
  offer: string;
  company: { name: string; country?: string | null; industry?: string | null };
  news: string;
  signals?: string;
}) =>
  [
    "A B2B company is looking for clients. From the news headlines and signals given, name the most recent dated fact",
    "that shows this lead needs the company's work now (a transformation, a new system, a tender, a reorganisation,",
    `growth) ${SIGNALS_NOTE}.`,
    'why_now: that fact with its date, under 30 words (for example "2026-09: announced a core banking replacement"),',
    'or "" when no headline shows such a need. A headline where the lead is the buyer, the partner or the subject of',
    "such a change counts, even when another organisation is named first. Do not guess and do not use headlines",
    "that are only about other organisations.",
    'Answer with JSON only: {"why_now": ""}',
    UNTRUSTED_NOTE,
    untrusted("what the company sells", input.offer),
    untrusted(
      "lead",
      [input.company.name, input.company.industry, input.company.country]
        .filter(Boolean)
        .join(", "),
    ),
    untrusted("news of the last year", input.news),
    input.signals &&
      untrusted(
        "signals (job ads, tenders, regulator rules, reports)",
        input.signals,
      ),
  ]
    .filter(Boolean)
    .join("\n");

export const DraftSchema = z.object({
  subject: z.string().trim().min(1).max(140),
  body: z.string().trim().min(1).max(3000),
  /** Names of the brochures to attach, most relevant first (older answers carry a single "brochure") */
  brochures: z.array(z.string().trim()).catch([]).default([]),
  brochure: z.string().trim().nullish(),
});
export type Draft = z.infer<typeof DraftSchema>;

/** Openers the draft prompts ban (a generic line instead of a fact about the lead), named in NO_GENERIC_OPENER */
const GENERIC_OPENERS = [
  /^as an? [^,.]{1,60},/i, // "As a government office, ..."
  /\byou (likely|probably|may|might) face\b/i,
  /^i hope (this|you)\b/i,
  /^(i am|i'm) reaching out\b/i,
  /^my name is\b/i,
];
const NO_GENERIC_OPENER =
  'Never open with a generic line such as "As a government office", "you likely face", "I hope this finds you well" or "I am reaching out".';
const PLACEHOLDER = /\[(sender|your|name|company|recipient)[^\]]*\]/i;

/** Why a drafted body cannot stand as written (a generic first sentence, a [Sender] placeholder), or null */
export const draftProblem = (body: string): string | null => {
  if (PLACEHOLDER.test(body)) return "The draft has a placeholder to fill in";
  const lines = body.split("\n").filter((l) => l.trim());
  const text = (GREETING.test(lines[0]?.trim() ?? "") ? lines.slice(1) : lines)
    .join(" ")
    .trim();
  const opener = text.match(/^[^.?!]*/)?.[0] ?? "";
  return GENERIC_OPENERS.some((re) => re.test(opener))
    ? "The draft opens with a generic line"
    : null;
};

/**
 * The first JSON object in the answer. Models sometimes add prose or a second object after it (ignored), or stop
 * before the last closing brackets (Llama on Workers AI drops the final "}"): those are closed when the answer
 * ends outside a string.
 */
export const extractJson = (text: string): unknown => {
  const start = text.indexOf("{");
  if (start < 0) throw new Error("AI answer has no JSON");
  const open: string[] = [];
  let inString = false;
  for (let i = start; i < text.length; i++) {
    const ch = text[i];
    if (inString) {
      if (ch === "\\") i++;
      else if (ch === '"') inString = false;
    } else if (ch === '"') inString = true;
    else if (ch === "{" || ch === "[") open.push(ch === "{" ? "}" : "]");
    else if ((ch === "}" || ch === "]") && open.pop() && !open.length)
      return JSON.parse(text.slice(start, i + 1));
  }
  if (inString) throw new Error("AI answer has no complete JSON");
  try {
    return JSON.parse(text.slice(start).trimEnd() + open.reverse().join(""));
  } catch {
    throw new Error("AI answer has no complete JSON");
  }
};

/** Who wrote a pre-lead draft (pre_lead_messages.draft_by) */
export type DraftBy = "gpt-oss" | "llama" | "claude" | "user";
/** The bridge answers as "claude", the Worker names its model; no name is its Llama fallback */
export const draftBy = (model: string): DraftBy =>
  model === "claude"
    ? "claude"
    : model.includes("gpt-oss")
      ? "gpt-oss"
      : "llama";

/**
 * A draft from the model, parsed and checked; a generic opener or a placeholder asks once more, and a second miss
 * is kept with its problem (the Draft tab shows it). Browser and outreach function both draft through this.
 */
export const writeDraft = async <S extends z.ZodType<{ body: string }>>(
  ask: (prompt: string, answered: (model: string) => void) => Promise<string>,
  prompt: string,
  schema: S,
) => {
  let model = "";
  const once = async () =>
    schema.parse(
      extractJson(await ask(prompt, (m) => (model = m))),
    ) as z.infer<S>;
  let draft = await once();
  let problem = draftProblem(draft.body);
  if (problem) {
    draft = await once();
    problem = draftProblem(draft.body);
  }
  return { draft, problem, draft_by: draftBy(model), draft_prompt: prompt };
};

export const buildDraftPrompt = (input: {
  /** the person who sends and signs (never the company name alone) */
  sender: string;
  company?: string;
  offer: string;
  lead: RankedLead;
  leadSite?: { url: string; text: string }[];
  brochures: Brochure[];
}) =>
  [
    `Write the first cold email from ${input.sender}${input.company ? ` of ${input.company}` : ""} to this lead. Under 120 words, plain text, no hype.`,
    "The first sentence names something concrete about this lead taken from its website or record: a service, a",
    `project, a market, its size or locations. ${NO_GENERIC_OPENER} Then link it to one specific problem the sender solves (from what the sender sells), and`,
    "end with one easy question (for example a 20-minute call). Greet the contact by first name when known, else no",
    `name. Sign as ${input.sender}, never with a placeholder such as [Sender]. Do not invent facts, numbers or clients. Write in English.`,
    input.brochures.length
      ? `brochures: the names of the brochures that fit this lead's need, at most ${MAX_BROCHURES}, most relevant first: the one service brochure for the problem the email raises, plus the company profile only when it adds something. Never attach a brochure for a service the email does not mention. Say in the email that it is attached. [] when none fits.`
      : "brochures: [].",
    'Answer with JSON only: {"subject": "...", "body": "...", "brochures": []}',
    UNTRUSTED_NOTE,
    untrusted("what the sender sells", input.offer),
    untrusted(
      "lead",
      [
        input.lead.lead.company.name,
        input.lead.lead.company.industry,
        [input.lead.lead.company.city, input.lead.lead.company.country]
          .filter(Boolean)
          .join(", "),
        input.lead.lead.contact &&
          `contact: ${input.lead.lead.contact.first_name} ${input.lead.lead.contact.last_name ?? ""}, ${input.lead.lead.contact.title ?? ""}`,
        `why it fits: ${input.lead.reason}`,
      ]
        .filter(Boolean)
        .join("\n"),
    ),
    input.leadSite?.length
      ? untrusted("lead website", pages(input.leadSite, LEAD_SITE_CHARS))
      : "",
    input.brochures.length
      ? untrusted(
          "brochures",
          input.brochures
            .map((b) => `${b.name}${b.about ? `: ${b.about}` : ""}`)
            .join("\n"),
        )
      : "",
  ]
    .filter(Boolean)
    .join("\n");

/** The brochures the draft named that are ours, in its order, without repeats, at most MAX_BROCHURES */
export const brochuresOf = (draft: Draft, brochures: Brochure[]) => {
  const key = (name: string) => name.trim().toLowerCase();
  const names = [
    ...draft.brochures,
    ...(draft.brochure ? [draft.brochure] : []),
  ].map(key);
  return [...new Set(names)]
    .flatMap((name) => brochures.filter((b) => key(b.name) === name))
    .slice(0, MAX_BROCHURES);
};

/** Brochures with a name and an https link; the rest are dropped on save */
export const cleanBrochures = (rows?: Partial<Brochure>[]): Brochure[] =>
  (rows ?? []).flatMap((row) => {
    const name = row?.name?.trim();
    const url = row?.url?.trim();
    if (!name || !url || !/^https:\/\/[^\s]+$/i.test(url)) return [];
    const about = row.about?.trim();
    return [{ name, url, ...(about && { about }) }];
  });

export const FollowupSchema = z.object({
  subject: z.string().trim().min(1).max(140),
  body: z.string().trim().min(1).max(3000),
});
export const ReplySchema = FollowupSchema;

type ThreadMessage = { subject: string; body: string };
const threadText = (thread: ThreadMessage[]) =>
  thread.map((m) => `${m.subject}\n${m.body}`).join("\n---\n");

export const buildFollowupPrompt = (input: {
  /** the person who sent the thread and signs */
  sender?: string | null;
  offer: string;
  lead: {
    company_name: string;
    contact_first_name?: string | null;
    contact_title?: string | null;
  };
  thread: ThreadMessage[];
  touch: number;
  instructions: string;
}) =>
  [
    `Write follow-up number ${input.touch - 1} to the cold email below, which got no reply. Under 70 words, plain text, no hype.`,
    "Do not repeat the first email. Add one new angle or one short question, keep the same thread subject with a Re: prefix, and never guilt-trip.",
    NO_GENERIC_OPENER,
    `Greet the contact by first name when known. Sign as ${input.sender || "the sender of the thread"}, never with a placeholder such as [Sender]. Do not invent facts, numbers or clients. Write in English.`,
    input.instructions,
    'Answer with JSON only: {"subject": "...", "body": "..."}',
    UNTRUSTED_NOTE,
    input.offer ? untrusted("what the sender sells", input.offer) : "",
    untrusted(
      "lead",
      [
        input.lead.company_name,
        input.lead.contact_first_name &&
          `contact: ${input.lead.contact_first_name}, ${input.lead.contact_title ?? ""}`,
      ]
        .filter(Boolean)
        .join("\n"),
    ),
    untrusted("thread", threadText(input.thread)),
  ]
    .filter(Boolean)
    .join("\n");

export const buildReplyPrompt = (input: {
  thread: ThreadMessage[];
  brochures: Brochure[];
  booking_line: string;
}) =>
  [
    "Draft a short reply from the sender to the prospect's last message in the thread below. Plain text, under 100 words, answer what they asked.",
    input.booking_line
      ? `When a call fits, end with this line: ${input.booking_line}`
      : "",
    input.brochures.length
      ? `Brochures you may point to by link: ${input.brochures.map((b) => `${b.name} ${b.url}`).join("; ")}`
      : "",
    "Do not invent facts, numbers or clients. Write in English.",
    'Answer with JSON only: {"subject": "...", "body": "..."}',
    UNTRUSTED_NOTE,
    untrusted("thread", threadText(input.thread)),
  ]
    .filter(Boolean)
    .join("\n");
